import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, Plus, FileWarning, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import PolicyReview from "@/components/action-center/PolicyReview";
import IncidentBuilder from "@/components/action-center/IncidentBuilder";

export function SupportNotice({ text }) {
  return <p data-testid="support-notice" role="note" className="text-xs text-[#7C3F00] bg-[#FFF7E8] border border-[#F5A623] rounded-lg p-3 leading-relaxed">{text}</p>;
}

const STATUS = { open: "bg-[#DBEAFE] text-[#0A2A5E]", decided: "bg-emerald-100 text-emerald-800", draft: "bg-slate-100 text-slate-700", submitted: "bg-[#FDECD0] text-[#7C3F00]", under_review: "bg-violet-100 text-violet-900", final: "bg-emerald-100 text-emerald-800" };
export const Pill = ({ s }) => <span data-testid={`status-${s}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS[s] || "bg-slate-100"}`}>{s.replace("_", " ")}</span>;

export default function ActionCenter() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [matters, setMatters] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [tab, setTab] = useState("matters");
  const [openMatter, setOpenMatter] = useState(null);
  const [openIncident, setOpenIncident] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: "", employee_name: "", summary: "" });
  const [denied, setDenied] = useState(false);

  const load = useCallback(() => {
    api.get("/action-center/matters").then((r) => setMatters(r.data.items)).catch(() => {});
    api.get("/action-center/incidents").then((r) => setIncidents(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/action-center/meta").then((r) => { setMeta(r.data); load(); }).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  }, [load]);

  const createMatter = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/action-center/matters", form); toast.success("Matter created"); setNewOpen(false); setForm({ title: "", employee_name: "", summary: "" }); load(); setOpenMatter(data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not create matter"); }
  };

  if (denied) return <div data-testid="action-center-denied" className="max-w-3xl mx-auto p-10 text-center text-slate-600">The Manager Center is available to HR managers and assigned reviewers only.</div>;
  if (!meta) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">HR Manager tools</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]" data-testid="manager-center-title">Manager Center</h1>
          <p className="text-sm text-slate-600 mt-1" data-testid="manager-center-description">Manager tools, guided HR workflows and workplace documentation support.</p>
        </div>
        {meta.is_hr && (
          <div className="flex flex-wrap gap-2">
            <Button data-testid="create-incident-btn" variant="outline" onClick={() => setOpenIncident("new")} className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]"><FileWarning className="w-4 h-4 mr-2" /> Create Incident Report</Button>
            <Button data-testid="new-matter-btn" onClick={() => setNewOpen(true)} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><Plus className="w-4 h-4 mr-2" /> New matter</Button>
          </div>
        )}
      </div>
      <SupportNotice text={meta.notices.support} />

      <div className="mt-6 mb-1" data-testid="action-center-section-heading">
        <h2 className="font-display text-lg font-bold text-[#0A2A5E]">Action Center</h2>
        <p className="text-xs text-slate-600">Policy-Guided Reviews and Incident Reports. The system never selects the final employment action — an authorized human does.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-2">
        <TabsList><TabsTrigger value="matters" data-testid="tab-matters">Matters ({matters.length})</TabsTrigger><TabsTrigger value="incidents" data-testid="tab-incidents">Incident reports ({incidents.length})</TabsTrigger></TabsList>
      </Tabs>

      {tab === "matters" && (
        <div className="mt-4 space-y-3">
          {matters.map((m) => (
            <Card key={m.id} data-testid={`matter-${m.id}`} className="p-4 bg-white border-slate-200 hover:border-[#F5A623] cursor-pointer" onClick={() => setOpenMatter(m)}>
              <div className="flex flex-wrap items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#7C3F00]" />
                <div className="flex-1 min-w-[12rem]"><div className="font-semibold text-[#0A2A5E]">{m.title}</div><div className="text-xs text-slate-500">{m.employee_name || "Employee not named"} · opened {m.created_at.slice(0, 10)} by {m.created_by_name}</div></div>
                {m.high_risk_categories?.length > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-rose-700"><ShieldAlert className="w-3.5 h-3.5" /> High-risk</span>}
                <Pill s={m.status} />
              </div>
            </Card>
          ))}
          {matters.length === 0 && <Card data-testid="matters-empty" className="p-8 text-center text-slate-500 bg-white">No matters yet. Create one to start a Policy-Guided Review.</Card>}
        </div>
      )}
      {tab === "incidents" && (
        <div className="mt-4 space-y-3">
          {incidents.map((i) => (
            <Card key={i.id} data-testid={`incident-${i.id}`} className="p-4 bg-white border-slate-200 hover:border-[#F5A623] cursor-pointer" onClick={() => setOpenIncident(i.id)}>
              <div className="flex flex-wrap items-center gap-3">
                <FileWarning className="w-5 h-5 text-[#7C3F00]" />
                <div className="flex-1 min-w-[12rem]"><div className="font-semibold text-[#0A2A5E]">{i.number} · {i.incident_type}</div><div className="text-xs text-slate-500">{i.location || "Location unknown"} · reported {i.reported_at.slice(0, 10)} by {i.created_by_name}{i.routed_to?.length ? ` · routed: ${i.routed_to.join(", ")}` : ""}</div></div>
                {i.high_risk && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-rose-700"><ShieldAlert className="w-3.5 h-3.5" /> High-risk</span>}
                <Pill s={i.status} />
              </div>
            </Card>
          ))}
          {incidents.length === 0 && <Card data-testid="incidents-empty" className="p-8 text-center text-slate-500 bg-white">No incident reports yet.</Card>}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">New matter</DialogTitle><DialogDescription>Describe the situation in neutral terms. You will record facts, context and sources in the Policy-Guided Review.</DialogDescription></DialogHeader>
          <form onSubmit={createMatter} className="space-y-3">
            <Input data-testid="matter-title" required placeholder="Matter title (e.g. Attendance concern and accommodation request)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Input data-testid="matter-employee" placeholder="Employee name (optional)" value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} />
            <Textarea data-testid="matter-summary" placeholder="Neutral summary of what is known so far" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            <div className="flex justify-end"><Button data-testid="matter-create-submit" type="submit" className="bg-[#0A2A5E] text-white">Create matter</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {openMatter && <PolicyReview matterId={openMatter.id} meta={meta} onClose={() => { setOpenMatter(null); load(); }} />}
      {openIncident && <IncidentBuilder incidentId={openIncident === "new" ? null : openIncident} meta={meta} user={user} onClose={() => { setOpenIncident(null); load(); }} />}
    </div>
  );
}
